import { createContext, useContext, useCallback } from 'react';
import { gql } from '@apollo/client';

import api from 'utils/api';

// Editor JWT mutations — used by the story editor, unrelated to user sign-in/sign-out
export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(input: { refreshToken: $refreshToken }) {
      jwt
      refreshToken
      errors {
        statusCode
        message
      }
    }
  }
`;

// TODO: Remove once we can access the JWT properly in a child component (probably once api.js is removed)
export const token_hack = { jwt: '' };

export type SignInResponse =
  | { step: 'password'; expireSessionOnClose: boolean }
  | { step: 'sso'; redirectUrl: string }
  | { success: true; redirectTo: string };

interface AuthContextType {
  signIn: (email: string, password?: string, rememberMe?: boolean) => Promise<SignInResponse>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  signIn: async () => ({ step: 'password', expireSessionOnClose: false }),
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const signIn = useCallback(
    async (email: string, password?: string, rememberMe?: boolean): Promise<SignInResponse> => {
      const response = await api.post('/signin.json', {
        user: { email, password, remember_me: rememberMe ? '1' : '0' },
      });

      // Guard against non-JSON responses (e.g., Devise middleware redirect for password_expired)
      if (typeof response.data !== 'object') {
        const finalUrl = (response.request as XMLHttpRequest)?.responseURL;
        if (finalUrl) window.location.href = finalUrl;
        throw new Error('Unexpected response from server');
      }

      return response.data;
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await api.get('/signout.json');
    } catch {
      // Non-critical — we redirect regardless of whether the API call succeeded
    }
    window.location.href = '/signin';
  }, []);

  return (
    <AuthContext.Provider value={{ signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
